import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Wallet, TrendingUp, Trophy, Target, Calculator, ShoppingCart, Package, FileText, ArrowUpRight,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import AccessNotice from "@/components/AccessNotice";
import { rupiah, rupiahShort, formatNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

function Kpi({ icon: Icon, label, value, sub, tone = "primary", testid }) {
  const tones = {
    primary: "bg-primary/10 text-primary",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    blue: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    teal: "bg-teal-500/10 text-teal-600 dark:text-teal-400",
  };
  return (
    <Card className="rounded-2xl border-border/80 p-5 shadow-sm transition-shadow hover:shadow-md" data-testid={testid}>
      <div className="flex items-start justify-between">
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-mono text-2xl font-extrabold tracking-tight sm:text-3xl">{value}</p>
      {sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}
    </Card>
  );
}

const ACTIONS = [
  { to: "/kalkulator", label: "Hitung HPP Produk", icon: Calculator },
  { to: "/kasir", label: "Buka Kasir / POS", icon: ShoppingCart },
  { to: "/resep", label: "Tambah Bahan Baku", icon: Package },
  { to: "/laporan", label: "Lihat Laporan", icon: FileText },
];

export default function Dashboard() {
  const { data: summary } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/summary")).data,
  });
  const { data: report } = useQuery({
    queryKey: ["report-mini"],
    queryFn: async () => (await api.get("/reports/summary")).data,
  });

  const chartData = (report?.daily || []).slice(-14);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Ringkasan bisnis Anda bulan ini.</p>
      </div>

      <AccessNotice />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
        <Kpi icon={Wallet} label="Omset Bulan Ini" tone="primary"
          value={rupiah(summary?.month_revenue)} sub={`${summary?.sales_count || 0} transaksi total`}
          testid="dashboard-monthly-revenue-kpi" />
        <Kpi icon={TrendingUp} label="Untung Bersih Bulan Ini" tone="teal"
          value={rupiah(summary?.month_profit)} sub="Estimasi laba dari penjualan"
          testid="dashboard-monthly-profit-kpi" />
        <Kpi icon={Trophy} label="Produk Terlaris" tone="amber"
          value={summary?.best_product ? summary.best_product.name : "-"}
          sub={summary?.best_product ? `${formatNumber(summary.best_product.quantity)} terjual` : "Belum ada penjualan"}
          testid="dashboard-best-product-kpi" />
        <Kpi icon={Target} label="Status Balik Modal (BEP)" tone="blue"
          value={`${summary?.bep_percent || 0}%`}
          sub={summary?.bep_reached ? "Modal sudah kembali 🎉" : "Menuju titik impas"}
          testid="dashboard-bep-kpi" />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="rounded-2xl p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold">Tren Omset (14 hari)</h3>
            <Link to="/laporan" className="flex items-center gap-1 text-sm font-medium text-primary hover:underline">
              Detail <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ left: -12, right: 8, top: 4 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tickFormatter={rupiahShort} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={64} />
                <Tooltip
                  formatter={(v) => [rupiah(v), "Omset"]}
                  contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }}
                />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--chart-1))" strokeWidth={2.5} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-2xl p-5">
          <h3 className="font-heading text-lg font-semibold">Aksi Cepat</h3>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {ACTIONS.map((a) => {
              const Icon = a.icon;
              return (
                <Link
                  key={a.to} to={a.to} data-testid={`quick-action-${a.to.slice(1)}`}
                  className="flex flex-col items-start gap-2 rounded-xl border border-border bg-secondary/40 p-4 transition-all hover:border-primary hover:bg-accent min-h-[44px]"
                >
                  <Icon className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium leading-tight">{a.label}</span>
                </Link>
              );
            })}
          </div>
          <div className="mt-5 rounded-xl bg-secondary/40 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Modal Awal</span>
              <span className="font-mono font-semibold">{rupiah(summary?.initial_capital)}</span>
            </div>
            <Progress value={summary?.bep_percent || 0} className="mt-3 h-2" />
            <p className="mt-2 text-xs text-muted-foreground">
              Total laba terkumpul: {rupiah(summary?.all_time_profit)}
            </p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card className="rounded-2xl p-4 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Resep/Produk</p>
          <p className="mt-1 font-mono text-2xl font-bold">{summary?.recipes_count || 0}</p>
        </Card>
        <Card className="rounded-2xl p-4 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Bahan Baku</p>
          <p className="mt-1 font-mono text-2xl font-bold">{summary?.materials_count || 0}</p>
        </Card>
        <Card className="rounded-2xl p-4 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Transaksi</p>
          <p className="mt-1 font-mono text-2xl font-bold">{summary?.sales_count || 0}</p>
        </Card>
        <Card className="rounded-2xl p-4 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Laba Total</p>
          <p className="mt-1 font-mono text-2xl font-bold text-primary">{rupiahShort(summary?.all_time_profit)}</p>
        </Card>
      </div>
    </div>
  );
}
